import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { siteMeta, contactInfo } from '../data';
import { imageUrl } from '../lib/images';

export default function Contact() {
  const [status, setStatus] = useState('');
  const [heroImages, setHeroImages] = useState([
    "/_assets/professionelt-skovarbejde-kontakt-mors-thy.jpg"
  ]);
  const [heroImageIndex, setHeroImageIndex] = useState(0);

  useEffect(() => {
    fetch('/_api/public/content/gallery')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then(data => {
        const allImages = data.flatMap(item => item.image || []);
        if (allImages.length > 0) {
          const shuffled = [...allImages].sort(() => Math.random() - 0.5);
          setHeroImages(shuffled);
        }
      })
      .catch(err => console.error('Error fetching gallery for hero:', err));
  }, []);

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const timer = setInterval(() => {
      setHeroImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroImages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <>
      <Helmet>
        <title>Kontakt | {siteMeta.title}</title>
        <meta name="description" content="Kontakt TH Skovservice for et uforpligtende tilbud på træfældning, topkapning eller skovservice." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-stone-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <AnimatePresence mode="wait">
            {heroImages.length > 0 && (
              <motion.img
                key={heroImageIndex}
                src={imageUrl(heroImages[heroImageIndex], 'width=1920,quality=85,format=auto')}
                alt="Kontakt TH Skovservice for uforpligtende tilbud på træfældning og skovarbejde"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                className="w-full h-full object-cover"
              />
            )}
          </AnimatePresence>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center pt-12">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[2rem] md:text-7xl font-bold mb-6 tracking-tight"
          >
            Kontakt Os
          </motion.h1>
          <p className="text-base md:text-xl text-stone-300 max-w-2xl mx-auto leading-relaxed">
            Har du spørgsmål eller ønsker du et uforpligtende tilbud? Vi sidder klar til at hjælpe dig med dit projekt.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl font-bold text-stone-900 mb-8">Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm text-stone-500 mb-1">Telefon</p>
                      <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="font-bold text-stone-900 hover:text-emerald-700 transition-colors">
                        {contactInfo.phone}
                      </a>
                    </div>
                    <div>
                      <p className="text-sm text-stone-500 mb-1">Email</p>
                      <a href={`mailto:${contactInfo.email}`} className="font-bold text-stone-900 hover:text-emerald-700 transition-colors break-all">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm text-stone-500 mb-1">Adresse</p>
                      <p className="font-bold text-stone-900">{contactInfo.address}</p>
                    </div>
                    <div>
                      <p className="text-sm text-stone-500 mb-1">Åbningstider</p>
                      <p className="font-bold text-stone-900">Man - Fre: 07:00 - 17:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-6">Følg os</h3>
                <a 
                  href={contactInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-3 bg-stone-100 text-stone-900 rounded-none font-bold hover:bg-emerald-900 hover:text-white transition-all"
                >
                  TH Skovservice på Facebook
                </a>
              </div>

              {/* Map Placeholder */}
              <div className="rounded-none overflow-hidden h-80 bg-stone-100 relative border border-stone-200">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2188.745484825656!2d8.5912345!3d56.7456789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46496f8a8a8a8a8a%3A0x8a8a8a8a8a8a8a8a!2sN%C3%A6ssundvej%20363%2C%207960%20Karby!5e0!3m2!1sda!2sdk!4v1620000000000!5m2!1sda!2sdk" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen="" 
                  loading="lazy"
                  title="Google Maps"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-stone-50 p-4 md:p-12 rounded-none border border-stone-100">
              <h2 className="text-3xl font-bold text-stone-900 mb-8">Send en besked</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <input 
                  type="text" 
                  name="website_url" 
                  className="sr-only absolute" 
                  tabIndex="-1" 
                  autoComplete="off" 
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-stone-700 ml-1">Navn</label>
                    <input 
                      required
                      type="text" 
                      name="name" 
                      placeholder="Dit fulde navn"
                      className="w-full px-6 py-4 rounded-none bg-white border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-stone-700 ml-1">Telefon</label>
                    <input 
                      required
                      type="tel" 
                      name="phone" 
                      placeholder="Dit telefonnummer"
                      className="w-full px-6 py-4 rounded-none bg-white border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-stone-700 ml-1">Email</label>
                  <input 
                    required
                    type="email" 
                    name="email" 
                    placeholder="Din email adresse"
                    className="w-full px-6 py-4 rounded-none bg-white border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-stone-700 ml-1">Besked</label>
                  <textarea 
                    required
                    name="message" 
                    rows="5"
                    placeholder="Fortæl os om din opgave..."
                    className="w-full px-6 py-4 rounded-none bg-white border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all resize-none"
                  ></textarea>
                </div>
                <button 
                  disabled={status === 'sending'}
                  className="w-full py-5 bg-emerald-600 text-white rounded-none font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-900/20 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {status === 'sending' ? 'Sender...' : 'Send besked'}
                </button>

                {status === 'success' && (
                  <motion.p 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-emerald-600 font-bold text-center"
                  >
                    Tak for din besked! Vi vender tilbage hurtigst muligt.
                  </motion.p>
                )}
                {status === 'error' && (
                  <p className="text-red-500 font-bold text-center">
                    Der skete en fejl. Prøv venligst igen eller ring til os.
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
